'use client'

import { FormEvent, useState } from 'react'
import { useRouter } from 'next/navigation'
import { H1 } from '@/General/H1'
import RetrieveDownloadLogin from '@/Card/RetrieveDownloadLogin'
import { useStore } from '@/store'
import { Input } from '@/General/Forms/Input'
import { Button } from '@/General/Button'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import {
  useValidateExplanationUniqueCodeMutation,
  ValidateExplanationUniqueCodeStatus,
} from '@/generated/graphql'

interface ExplanationLoginRequiredProps {
  slug: string
  productName?: string | null
  redirectPath: string
}

export const ExplanationLoginRequired = ({
  slug,
  productName,
  redirectPath,
}: ExplanationLoginRequiredProps) => {
  const [code, setCode] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)
  const userId = useStore(state => state.user?.id)
  const router = useRouter()

  const { mutateAsync: validateCode, isPending } =
    useValidateExplanationUniqueCodeMutation()

  const getStatusMessage = (
    status?: ValidateExplanationUniqueCodeStatus | null,
    fallback?: string | null
  ) => {
    switch (status) {
      case ValidateExplanationUniqueCodeStatus.InvalidCode:
        return 'Sadly that code isn’t valid. Please try again.'
      case ValidateExplanationUniqueCodeStatus.NotActivated:
        return 'This code has not yet been activated. Please contact Vanishing Inc.'
      case ValidateExplanationUniqueCodeStatus.WrongProduct:
        return 'This code does not match this explanation.'
      case ValidateExplanationUniqueCodeStatus.ExplanationNotFound:
        return 'We couldn’t find an explanation for that code. Please double-check and try again.'
      default:
        return (
          fallback ||
          'We were unable to verify that code. Please double-check it and try again.'
        )
    }
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedCode = code.trim()

    if (!trimmedCode) {
      setFeedback('Please enter the code from the card.')
      return
    }

    try {
      const response = await validateCode({
        slug,
        code: trimmedCode,
      })

      const validation = response?.validateExplanationUniqueCode
      if (!validation) {
        setFeedback('We were unable to verify that code. Please try again.')
        return
      }

      if (validation.status !== ValidateExplanationUniqueCodeStatus.Valid) {
        setFeedback(getStatusMessage(validation.status, validation.message))
        return
      }

      if (validation.requiresLogin && !userId) {
        setFeedback('Please log in to your Vanishing Inc. account to continue.')
        return
      }

      setFeedback(null)
      router.push('/account/downloads/?msg=downloadcard')
    } catch (error: any) {
      const graphQLError = error?.response?.errors?.[0]?.message
      setFeedback(
        graphQLError ||
          error?.message ||
          'We were unable to verify that code. Please try again.'
      )
    }
  }

  if (!userId) {
    return (
      <div className="w-full max-w-3xl mx-auto">
        <RetrieveDownloadLogin redirectTo={redirectPath} />
      </div>
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      <section className="w-full rounded-md bg-gray-100 px-6 py-8 md:px-10 md:py-10">
        <H1 className="text-gray-900">Retrieve your download</H1>
        <p className="mt-4 max-w-3xl text-base text-gray-700">
          Enter the unique code from your download card to unlock{' '}
          {productName ?? 'this download'}. Once verified, the download will be
          added to your account automatically.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <ErrorBox errors={feedback ? [feedback] : []} />
          <Input
            id="explanation-unique-code"
            type="text"
            value={code}
            onChange={event => setCode(event.target.value)}
            placeholder="Enter your unique code"
            isError={Boolean(feedback)}
          />

          <Button
            type="submit"
            disabled={isPending || !code.trim()}
            color="green"
            size="md"
            className="w-full md:w-fit"
          >
            {isPending ? 'Processing…' : 'Verify code'}
          </Button>
        </form>
      </section>
    </div>
  )
}
